import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Container, Box } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchGroups,
  fetchLightweightUsersV4,
  fetchLinkedServices,
  selectGroupById,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  selectLinkedServicesStatus,
  selectGroupsStatus,
} from '../manage.slice'
import { transformUsersToRows } from './utils/user-transforms'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import DetailsHeaderActionButtons from '../components/edit-delete-buttons'
import { deleteGroup } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { GROUPS_URLS } from '../constants'
import GroupDetailsCard from './components/group-details-card'
import GroupUsersCard from './components/group-users-card'

export default function GroupDetailV4(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let location = useLocation()
  let { groupID } = useParams<{ groupID: string }>()

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [isDeleting, setIsDeleting] = useState<boolean>(false)
  let [dialogStatus, setDialogStatus] = useState<string>('')

  let group = useAppSelector(selectGroupById(groupID || ''))
  let fetchGroupsStatus = useAppSelector(selectGroupsStatus)
  let users = useAppSelector(
    selectLightweightUsersV4ByService(group?.linkedServiceID || ''),
  )
  let fetchUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(group?.linkedServiceID || ''),
  )
  let fetchLinkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let userRows = useMemo(() => transformUsersToRows(users), [users])
  let groupUsers = useMemo(() => {
    if (!group || !group.users) return []
    return userRows.filter((u) => group.users!.includes(u.id))
  }, [userRows, group])

  let linkedServiceName = useLinkedServiceName(group?.linkedServiceID)

  let handleEdit = useCallback(() => {
    navigate(`${GROUPS_URLS.INDEX}/${groupID}/edit`)
  }, [navigate, groupID])

  let handleDelete = useCallback(async () => {
    if (!group?.id) return

    setIsDeleting(true)

    await deleteGroup(group.id)
      .then(() => dispatch(fetchGroups()))
      .then(() => {
        navigate(GROUPS_URLS.INDEX, {
          state: {
            successMessage: `Group "${group.name}" deleted successfully`,
            linkedServiceID: group.linkedServiceID,
          },
        })
      })
      .catch((err: any) => {
        let errorMessage = `Error deleting group "${group.name}".`
        // eslint-disable-next-line no-console
        console.error(errorMessage, 'Group ID: ', group.id, 'Error', err)
        showError(errorMessage)
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }, [group, dispatch, navigate, showError])

  let isLoading =
    fetchGroupsStatus === 'pending' ||
    fetchUsersStatus === 'pending' ||
    fetchLinkedServicesStatus === 'pending'

  useAutoFetch(fetchGroupsStatus, fetchGroups)
  useAutoFetch(fetchLinkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (group?.linkedServiceID && fetchUsersStatus === 'uninitialized') {
      dispatch(fetchLightweightUsersV4(group.linkedServiceID))
    }
  }, [group?.linkedServiceID, fetchUsersStatus, dispatch])

  useEffect(() => {
    if (location.state?.successMessage) {
      showSuccess(location.state.successMessage)
      // Clear the message so it doesn't show again on navigation
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, navigate, showSuccess])

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <PageTitleWithLinkedService
          pageTitle={group?.name ?? ''}
          linkedServiceName={linkedServiceName ?? ''}
          moduleName="Groups"
          handleBackClick={() =>
            navigate(GROUPS_URLS.INDEX, {
              state: { linkedServiceID: group?.linkedServiceID },
            })
          }
          isLoading={isLoading || !group}
        />
        <DetailsHeaderActionButtons
          moduleName="Group"
          canSubmit={true}
          isSubmitting={false}
          isDisabled={isLoading || isDeleting}
          handleEdit={handleEdit}
          handleDelete={() => setDialogStatus('delete')}
        />
      </Box>
      <GroupDetailsCard
        group={group || null}
        linkedServiceName={linkedServiceName}
        isLoading={isLoading}
      />
      <GroupUsersCard users={groupUsers} isLoading={isLoading} />
      <DeleteConfirmationModal
        isOpen={dialogStatus === 'delete'}
        onClose={() => setDialogStatus('')}
        onConfirm={handleDelete}
        entityName="group"
        entityDisplayName={group?.name || ''}
        isSubmitting={isDeleting}
      />
      {NotificationComponent}
    </Container>
  )
}
